import { useMemo, useState } from "react";
import Select from "react-select";

// ============================================
// REUSABLE COMPONENT: SelectAll (Search Aware)
// ============================================
const SelectAll = ({
    options = [],
    value,
    onChange,
    selectAllLabel = "Select All",
    ...props
}) => {
    const [inputValue, setInputValue] = useState("");

    const SELECT_ALL_VALUE = "__select_all__";

    const selectAllOption = {
        value: SELECT_ALL_VALUE,
        label: selectAllLabel,
    };

    // --------------------------------------------
    // Normalize values (react-select safety)
    // --------------------------------------------
    const currentValue = useMemo(
        () => (Array.isArray(value) ? value : []),
        [value]
    );

    // --------------------------------------------
    // Filter options based on search input
    // --------------------------------------------
    const filteredOptions = useMemo(() => {
        if (!inputValue) return options;

        const search = inputValue.toLowerCase();

        return options.filter((opt) =>
            opt.label?.toLowerCase().includes(search)
        );
    }, [options, inputValue]);

    // --------------------------------------------
    // Options shown in dropdown
    // --------------------------------------------
    const allOptions =
        options.length > 0 ? [selectAllOption, ...filteredOptions] : [];

    // --------------------------------------------
    // Change handler
    // --------------------------------------------
    const handleChange = (selected) => {
        const selectedValues = Array.isArray(selected) ? selected : [];

        const selectAllClicked = selectedValues.some(
            (opt) => opt.value === SELECT_ALL_VALUE
        );

        if (!selectAllClicked) {
            onChange(selectedValues);
            return;
        }

        // ----------------------------------------
        // Toggle select all for FILTERED options
        // ----------------------------------------
        const isAllFilteredSelected =
            filteredOptions.length > 0 &&
            filteredOptions.every((fo) =>
                currentValue.some((cv) => cv.value === fo.value)
            );

        if (isAllFilteredSelected) {
            // Remove only filtered items
            const remaining = currentValue.filter(
                (cv) => !filteredOptions.some((fo) => fo.value === cv.value)
            );
            onChange(remaining);
        } else {
            // Add filtered items (avoid duplicates)
            const merged = [
                ...currentValue,
                ...filteredOptions.filter(
                    (fo) => !currentValue.some((cv) => cv.value === fo.value)
                ),
            ];
            onChange(merged);
        }
    };

    // --------------------------------------------
    // Always keep "Select All" visible while search
    // --------------------------------------------
    const filterOption = (option, input) => {
        if (option.value === SELECT_ALL_VALUE) return true;
        return option.label?.toLowerCase().includes(input.toLowerCase());
    };

    // --------------------------------------------
    // Styling (optional highlight)
    // --------------------------------------------
    const customStyles = {
        option: (base, state) => ({
            ...base,
            fontWeight: state.data.value === SELECT_ALL_VALUE ? 600 : 400,
            borderBottom:
                state.data.value === SELECT_ALL_VALUE
                    ? "1px solid #e5e7eb"
                    : "none",
            backgroundColor: state.isSelected
                ? "#2684FF"
                : state.isFocused
                ? "#e0e7ff"
                : "white",
            color: state.isSelected ? "white" : "#1f2937",
        }),
        ...props.styles,
    };

    return (
        <Select
            {...props}
            isMulti
            options={allOptions}
            value={currentValue}
            onChange={handleChange}
            onInputChange={setInputValue}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            filterOption={filterOption}
            styles={customStyles}
        />
    );
};

export default SelectAll;
